import React, { useState } from 'react';
import { Bell, Check, CheckCheck, Trash2, Package2, Send, CheckCircle, XCircle, AlertTriangle, MessageSquare, Eye, Upload, ClipboardList, CheckSquare, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useNotifications } from '@/hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

export function NotificationCenter() {
  const { notifications, loading, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'task_assigned':
        return <ClipboardList className="h-4 w-4 text-primary" />;
      case 'task_completed':
        return <CheckSquare className="h-4 w-4 text-success" />;
      case 'task_comment':
        return <MessageCircle className="h-4 w-4 text-info" />;
      case 'payment_proof_uploaded':
        return <Upload className="h-4 w-4 text-status-info" />;
      case 'sale_comment':
        return <MessageSquare className="h-4 w-4 text-primary" />;
      case 'transfer_received':
        return <Package2 className="h-4 w-4 text-success" />;
      case 'transfer_sent':
        return <Send className="h-4 w-4 text-info" />;
      case 'transfer_approved':
        return <CheckCircle className="h-4 w-4 text-success" />;
      case 'transfer_rejected':
        return <XCircle className="h-4 w-4 text-destructive" />;
      case 'transfer_approval_needed':
        return <AlertTriangle className="h-4 w-4 text-warning" />;
      case 'transfer_updated':
        return <Bell className="h-4 w-4 text-info" />;
      case 'support_ticket_created':
        return <MessageSquare className="h-4 w-4 text-warning" />;
      case 'support_ticket_read':
        return <Eye className="h-4 w-4 text-info" />;
      default:
        return <Bell className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-destructive/10 border-destructive/20';
      case 'high':
        return 'bg-warning/10 border-warning/20';
      case 'normal':
        return 'bg-info/10 border-info/20';
      case 'low':
        return 'bg-muted border-border';
    }
  };

  const handleNotificationClick = (notification: any) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
  };

  if (loading) {
    return (
      <Button variant="ghost" size="sm" disabled>
        <Bell className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Notificaciones</CardTitle>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={markAllAsRead}
                    className="text-xs"
                  >
                    <CheckCheck className="h-3 w-3 mr-1" />
                    Marcar todas
                  </Button>
                )}
                <Badge variant="secondary" className="text-xs">
                  {unreadCount} nuevas
                </Badge>
              </div>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="p-0">
            <ScrollArea className="h-96">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Bell className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No hay notificaciones</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-3 border-l-4 cursor-pointer transition-colors hover:bg-muted/50 ${
                        !notification.is_read ? 'bg-info/5' : ''
                      } ${getPriorityColor(notification.priority)}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start gap-3">
                        {getNotificationIcon(notification.type)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm font-medium truncate ${
                              !notification.is_read ? 'text-foreground' : 'text-muted-foreground'
                            }`}>
                              {notification.title}
                            </p>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {!notification.is_read && (
                                <div className="h-2 w-2 bg-info rounded-full" />
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteNotification(notification.id);
                                }}
                                className="h-6 w-6 p-0 hover:bg-destructive/10"
                              >
                                <Trash2 className="h-3 w-3 text-destructive" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(notification.created_at), {
                                addSuffix: true,
                                locale: es
                              })}
                            </p>
                            {notification.data?.transfer_code && (
                              <Badge variant="outline" className="text-xs">
                                {notification.data.transfer_code}
                              </Badge>
                            )}
                            {notification.data?.ticket_number && (
                              <Badge variant="outline" className="text-xs">
                                {notification.data.ticket_number}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </PopoverContent>
    </Popover>
  );
}